import {
  useState, useRef, useCallback,
  ChangeEvent, DragEvent,
} from "react";
import { QueuedFile, ToastState } from "./types";
import { ACCEPTED_MIME, FOLDERS, MAX_FILE_SIZE, MAX_FILES } from "./constants";
import { AcceptedTypes, detectType, fmtSize, QueueItem, uid, UploadTips } from "./helpers";

export default function UploadNew() {
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [defaultFolder, setDefaultFolder] = useState("products");
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ visible: false, message: "", type: "success" });
  const inputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string, type: ToastState["type"] = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, message, type });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3200);
  };

  // ── File ingestion ────────────────────────────────────────────────────────

  const ingestFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const errors: string[] = [];

    if (queue.length + arr.length > MAX_FILES) {
      errors.push(`Max ${MAX_FILES} files per batch. Excess files skipped.`);
    }

    const newItems: QueuedFile[] = [];
    const allowed = arr.slice(0, Math.max(0, MAX_FILES - queue.length));

    for (const file of allowed) {
      if (file.size > MAX_FILE_SIZE) { errors.push(`"${file.name}" exceeds 100 MB limit.`); continue; }
      const type = detectType(file);
      const preview = type === "image" ? URL.createObjectURL(file) : "";
      newItems.push({
        id: uid(), file, type, previewUrl: preview,
        status: "queued", progress: 0, error: null,
        folder: defaultFolder, altText: "", overrideName: "",
      });
    }

    setQueue((q) => [...q, ...newItems]);
    if (errors.length) showToast(errors[0], "error");
  }, [queue.length, defaultFolder]);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDragging(false);
    ingestFiles(e.dataTransfer.files);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) ingestFiles(e.target.files);
    e.target.value = "";
  };

  // ── Queue mutations ───────────────────────────────────────────────────────

  const updateItem = (id: string, patch: Partial<QueuedFile>) =>
    setQueue((q) => q.map((item) => item.id === id ? { ...item, ...patch } : item));

  const removeItem = (id: string) => {
    setQueue((q) => {
      const item = q.find((f) => f.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return q.filter((f) => f.id !== id);
    });
    setExpanded((s) => { const n = new Set(s); n.delete(id); return n; });
  };

  const clearDone = () => {
    setQueue((q) => {
      q.filter((f) => f.status === "done").forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      return q.filter((f) => f.status !== "done");
    });
  };

  const clearAll = () => {
    queue.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    setQueue([]); setExpanded(new Set());
  };

  const toggleExpand = (id: string) =>
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── Simulated upload ──────────────────────────────────────────────────────

  const simulateUpload = async (item: QueuedFile) => {
    return new Promise<void>((resolve) => {
      updateItem(item.id, { status: "uploading", progress: 0 });
      const duration = 1500 + Math.random() * 2000;
      const steps = 20;
      const interval = duration / steps;
      let step = 0;

      // Simulate occasional error (~10%)
      const willError = Math.random() < 0.1;
      const errorAt = Math.floor(steps * 0.5 + Math.random() * steps * 0.4);

      const timer = setInterval(() => {
        step++;
        if (willError && step === errorAt) {
          clearInterval(timer);
          updateItem(item.id, { status: "error", progress: Math.round((step / steps) * 100), error: "Upload failed — server error. Please retry." });
          resolve();
          return;
        }
        const progress = Math.min(100, Math.round((step / steps) * 100));
        updateItem(item.id, { progress });
        if (step >= steps) {
          clearInterval(timer);
          updateItem(item.id, { status: "done", progress: 100 });
          resolve();
        }
      }, interval);
    });
  };

  const handleUploadAll = async () => {
    const pending = queue.filter((f) => f.status === "queued" || f.status === "error");
    if (!pending.length) return;
    setUploading(true);
    await Promise.all(pending.map((item) => simulateUpload(item)));
    setUploading(false);
    const doneCount = pending.length;
    showToast(`${doneCount} file${doneCount > 1 ? "s" : ""} processed`, "success");
  };

  // ── Derived counts ────────────────────────────────────────────────────────

  const queuedCount = queue.filter((f) => f.status === "queued").length;
  const uploadingCount = queue.filter((f) => f.status === "uploading").length;
  const doneCount = queue.filter((f) => f.status === "done").length;
  const errorCount = queue.filter((f) => f.status === "error").length;
  const totalSize = queue.reduce((s, f) => s + f.file.size, 0);
  const canUpload = (queuedCount + errorCount) > 0 && !uploading;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --input-bg:rgba(255,255,255,0.035); --section-header:rgba(255,255,255,0.02);
        //   --surface:#111110;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder, textarea::placeholder { color:var(--text3); }
        input, textarea, select { color:var(--text); }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes pulse       { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes dropPulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.02)} }
        .uf-layout { display:grid; grid-template-columns:1fr 288px; gap:24px; align-items:start; }
        .uf-sidebar { display:flex; flex-direction:column; gap:14px; position:sticky; top:24px; }
        @media(max-width:1080px){.uf-layout{grid-template-columns:1fr;}.uf-sidebar{position:static;}}
      `}</style>

      {/* Toast */}
      {toast.visible && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 600,
          background: toast.type === "success" ? "#1a2e1a" : toast.type === "info" ? "#1a1f2e" : "#2e1a1a",
          border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : toast.type === "info" ? "rgba(138,180,248,0.25)" : "rgba(224,82,82,0.25)"}`,
          borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
          animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : toast.type === "info" ? "#8ab4f8" : "#e08b8b" }}>
            {toast.type === "success" ? "✓" : toast.type === "info" ? "ℹ" : "✕"}&nbsp;&nbsp;{toast.message}
          </span>
        </div>
      )}

      <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>
                File Manager
              </div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>Upload Files</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                Drag and drop files below, or click to browse. Up to {MAX_FILES} files per batch.
              </p>
            </div>
            {queue.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {doneCount > 0 && (
                  <button onClick={clearDone} style={{
                    fontSize: 13, padding: "8px 14px", borderRadius: 8,
                    border: "1px solid var(--border)", background: "transparent",
                    color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                  }}>Clear done ({doneCount})</button>
                )}
                <button onClick={clearAll} style={{
                  fontSize: 13, padding: "8px 14px", borderRadius: 8,
                  border: "1px solid rgba(224,82,82,0.3)", background: "rgba(224,82,82,0.06)",
                  color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                }}>Clear all</button>
              </div>
            )}
          </div>

          <div className="uf-layout">

            {/* ── Left: drop zone + queue ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

              {/* Default folder selector */}
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                background: "var(--card)", border: "1px solid var(--border)",
                borderRadius: 12, padding: "12px 16px",
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", whiteSpace: "nowrap" }}>
                  Default folder
                </span>
                <select
                  value={defaultFolder}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setDefaultFolder(e.target.value)}
                  style={{
                    flex: 1, fontSize: 13, padding: "7px 12px", borderRadius: 8,
                    border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)",
                    color: "var(--or)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none",
                  }}
                >
                  {FOLDERS.map((f) => <option key={f} value={f} style={{ background: "#1a1917", color: "#e8e6de" }}>{f}</option>)}
                </select>
                <span style={{ fontSize: 12, color: "var(--text3)" }}>Applied to new files. Override per file below.</span>
              </div>

              {/* Drop zone */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "16px 20px" }}>
                <div
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  style={{
                    border: `2px dashed ${dragging ? "var(--or)" : "var(--border)"}`,
                    borderRadius: 16, padding: "44px 24px",
                    background: dragging ? "rgba(191,61,12,0.06)" : "rgba(255,255,255,0.015)",
                    cursor: "pointer", textAlign: "center",
                    transition: "all 0.2s",
                    animation: dragging ? "dropPulse 0.6s ease-in-out infinite" : "none",
                  }}
                >
                  {/* Upload icon */}
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px",
                    background: dragging ? "var(--or-dim)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${dragging ? "rgba(191,61,12,0.3)" : "var(--border)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                      stroke={dragging ? "var(--or)" : "var(--text3)"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: dragging ? "var(--or)" : "var(--text2)", marginBottom: 6 }}>
                    {dragging ? "Release to add files" : "Drop files here"}
                  </div>
                  <div style={{ fontSize: 14, color: "var(--text3)", marginBottom: 16 }}>
                    or click to browse your device
                  </div>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, color: "var(--text3)",
                    background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
                    borderRadius: 20, padding: "4px 12px",
                  }}>
                    <span>JPG · PNG · PDF · DOCX · XLSX · MP4 · ZIP</span>
                    <span style={{ color: "var(--border)" }}>·</span>
                    <span>Max 100 MB</span>
                  </div>
                </div>
              </div>
              <input ref={inputRef} type="file" multiple accept={Object.keys(ACCEPTED_MIME).join(",")} onChange={onInputChange} style={{ display: "none" }} />

              {/* Queue */}
              {queue.length > 0 && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 0, height: '50vh', overflow: "auto" }}>
                    {/* Queue header */}
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 10,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
                          Upload queue
                        </span>
                        <div style={{ display: "flex", gap: 6 }}>
                          {queuedCount > 0 && <span style={{ fontSize: 11, background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", color: "var(--text3)", borderRadius: 20, padding: "2px 8px" }}>{queuedCount} queued</span>}
                          {uploadingCount > 0 && <span style={{ fontSize: 11, background: "rgba(245,166,35,0.10)", border: "1px solid rgba(245,166,35,0.25)", color: "#f5a623", borderRadius: 20, padding: "2px 8px" }}>{uploadingCount} uploading</span>}
                          {doneCount > 0 && <span style={{ fontSize: 11, background: "rgba(109,220,109,0.10)", border: "1px solid rgba(109,220,109,0.25)", color: "#6ddc6d", borderRadius: 20, padding: "2px 8px" }}>{doneCount} done</span>}
                          {errorCount > 0 && <span style={{ fontSize: 11, background: "rgba(224,82,82,0.10)", border: "1px solid rgba(224,82,82,0.25)", color: "#e05252", borderRadius: 20, padding: "2px 8px" }}>{errorCount} failed</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, color: "var(--text3)" }}>{fmtSize(totalSize)} total</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {queue.map((item) => (
                        <QueueItem
                          key={item.id} item={item}
                          expanded={expanded.has(item.id)}
                          onToggleExpand={() => toggleExpand(item.id)}
                          onRemove={() => removeItem(item.id)}
                          onFolderChange={(f) => updateItem(item.id, { folder: f })}
                          onAltChange={(v) => updateItem(item.id, { altText: v })}
                          onNameChange={(v) => updateItem(item.id, { overrideName: v })} />
                      ))}
                    </div>

                    {/* Upload button */}
                  </div>
                </>
              )}
            <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => inputRef.current?.click()} style={{
                padding: "10px 18px", borderRadius: 9, border: "1px solid var(--border)",
                background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
              }}>+ Add more</button>
              <button
                onClick={handleUploadAll}
                disabled={!canUpload}
                style={{
                  padding: "10px 24px", borderRadius: 9,
                  border: `1px solid ${canUpload ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
                  background: canUpload ? "linear-gradient(135deg,#BF3D0C,#E8593C)" : "rgba(255,255,255,0.04)",
                  color: canUpload ? "#fff" : "var(--text3)", fontSize: 14, fontWeight: 500,
                  cursor: canUpload ? "pointer" : "not-allowed",
                  fontFamily: "'DM Sans',sans-serif",
                  boxShadow: canUpload ? "0 2px 12px rgba(191,61,12,0.25)" : "none",
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", gap: 7,
                }}
              >
                {uploading ? (
                  <>
                    <span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                    Uploading…
                  </>
                ) : (
                  <>{errorCount > 0 ? "Retry failed" : "Upload all"} ({queuedCount + errorCount})</>
                )}
              </button>
            </div>
            </div>

            {/* ── Right: sidebar ── */}
            <aside className="uf-sidebar">
              <AcceptedTypes />
              <UploadTips />
            </aside>

          </div>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}